import { useState } from "react";
import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASONS = [
  { value: "scope", label: "Not relevant" },
  { value: "methodology", label: "Methodology" },
  { value: "superseded", label: "Superseded" },
  { value: "duplicate", label: "Duplicate" },
  { value: "not_found", label: "Not found" },
  { value: "other", label: "Other" },
] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  onConfirm: (reason: string, reasonText?: string) => void;
}

export function BulkDismissDialog({ open, onOpenChange, count, onConfirm }: Props) {
  const [selected, setSelected] = useState<string | null>("scope");
  const [reasonText, setReasonText] = useState("");

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm(selected, selected === "other" ? reasonText || undefined : undefined);
    onOpenChange(false);
    setSelected("scope");
    setReasonText("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dismiss {count} paper{count !== 1 ? "s" : ""}?</DialogTitle>
          <DialogDescription>
            Select a reason for dismissal. Dismissed papers can be restored later.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 py-2">
          {REASONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setSelected(r.value)}
              className={`rounded px-3 py-2 text-left text-sm transition-colors ${
                selected === r.value
                  ? "bg-accent text-accent-foreground"
                  : "hover:bg-muted"
              }`}
            >
              {r.label}
            </button>
          ))}
          {selected === "other" && (
            <input
              type="text"
              placeholder="Reason..."
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              className="mt-1 rounded border bg-background px-3 py-2 text-sm"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selected} onClick={handleConfirm}>
            <Ban className="mr-2 size-4" />
            Dismiss {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
